import UserPicker from '@ebg/vue-common/src/components/user/picker/UserPicker.vue';
import OnboardingChecklistParams from '@/components/user/onboarding/checklist/params/OnboardingChecklistParams.vue';
import EbgSystemUserGroups from '@/components/ebgsystems/usergroups/EbgSystemUserGroups.vue';

const defaultFilterValues = {
  type: null,
  name: null
};

export default {
  name: 'OnboardingChecklist',
  props: {
    userId: {
      default: null
    },
    userApiId: {
      default: null
    },
    jobPosition: {
      default: null
    },
    mode: {
      validator (value) {
        return ['onboarding', 'offboarding'].includes(value);
      },
      default: 'onboarding'
    }
  },
  data () {
    return {
      filter: {
        name: null,
        surname: null
      },
      groupValues: null,
      user: null,
      isLoading: false,
      isUpdating: false,
      showAdditionalCheckboxes2: false,
      jobPositionWorkType: 1, // birojs,
      paging: { limit: 20, page: 1, total: 0, pages: 0 },
      filterValues: {
        type: [],
        name: [],
        displayColFilters: true
      },
      currentReq: null,
      existing_group_ids: [],
      changeStatusShowComment: false,
      changeStatusCommentValue: null,
      canDelete: false,
      mailPreview: {},
      isLoadingMailPreview: false
    }
  },
  components: {
    EbgSystemUserGroups,
    OnboardingChecklistParams,
    UserPicker
  },
  computed: {
    statusSentMailId () {
      return this.BasicHelper.getConfigValue('onboarding_statuses', 'code', 'SENT_EMAIL', 'id');
    },
    markedReturnStatusIds () {
      return [
        this.BasicHelper.getConfigValue('onboarding_statuses', 'code', 'RETURNED', 'id'),
        this.BasicHelper.getConfigValue('onboarding_statuses', 'code', 'NOT_RETURNED', 'id')
      ];
    },
    // Read the role names the login itself stores - the full 'user' arrives only with the first session
    // check, so a manager coming in through the mail link would otherwise see an empty checklist
    // until the page was reloaded (HR-338).
    isUserHR () {
      return (this.$session.get('roles') || []).some(role => ['op', 'admin'].includes(role));
    },
    systemPermissions () {
      if (this.user && this.user.ad_group_permissions) {
        return this.user.ad_group_permissions.systems;
      }
      return null;
    },
    requestPath () {
      let path = `user/${this.userApiId}/onboarding?perPage=${this.paging.limit}&page=${this.paging.page}`;
      if (this.mode === 'offboarding') {
        path = path + `&mode=offboarding&job_position=${this.jobPosition}`
      }

      return this.ApiRequest.addGetParamsToPath(path, this.filterValues, true);
    },
    tableExpandConfig () {
      return { component: 'slot' };
    },
    tableHeaders () {
      const headers = [
        { key: 'code', label: this.$t('Kontrolsaraksts'), type: 'slot', slotcode: 'checklist', filterKey: 'name' },
        { key: 'id', label: this.$t('Darbības'), type: 'slot', slotcode: 'actions' },
        { key: 'status_id', label: this.$t('Statuss'), type: 'slot', slotcode: 'status' },
        { key: 'approvers', label: this.$t('Apstiprinātājs'), type: 'slot', slotcode: 'approvers' },
        { key: 'date', label: this.$t('Datums'), type: 'slot', slotcode: 'date' }
      ];
      const actions = [
        {
          label: this.$t('general.actions.delete'),
          emit: 'deleteItem',
          bicon: 'trash',
          class: 'btn-sm btn-danger',
          ifconfig: { useFunc: true, func: this.canDeleteItem }
        }
      ];
      headers.push({ key: 'actions', label: '', actions: actions, headerClass: 'col-1' });
      return headers;
    },
    tableFilterConfig () {
      const filters = { };
      if (this.mode === 'onboarding') {
        filters.type = { type: 'select', options: this.checklistTypes, colOnlyFilter: true };
      }
      if (this.isUserHR) {
        filters.name = { type: 'select', options: this.existingGroups, colOnlyFilter: true };
      }
      return filters;
    },
    checklistTypes () {
      return this.$store.getters.getConfigOptions('onboarding_types', this.$i18n.locale);
    },
    checklistGroups () {
      return this.$store.getters.getConfigOptions('onboarding_groups', this.$i18n.locale);
    },
    statuses () {
      return this.$store.getters.getConfigOptions('onboarding_statuses', this.$i18n.locale);
    },
    groups () {
      const offboardingTypeId = this.$store.getters.getParamFromConfigByFilter('onboarding_types', 'code', 'EXIT', 'id');
      return this.$store.getters.getConfigOptions('onboarding_groups', this.$i18n.locale, null, null, false, false, false, ['type_id'])
        .filter(el => el.type_id !== offboardingTypeId);
    },
    existingGroups () {
      return this.$store.getters.getConfigOptions('onboarding_groups', this.$i18n.locale).filter(el => this.existing_group_ids.includes(el.value));
    },
    showAdditionalCheckboxes () {
      return this.$store.getters.getConfigOptions('onboarding_groups', this.$i18n.locale).reduce((acc, item) => {
        acc[item.value] = { show: false };
        return acc;
      }, {});
    },
    onboardingGroupStatuses () {
      return this.$store.getters.getConfigOptions('onboarding_group_status', this.$i18n.locale, null, null, false, false, false, ['group_id', 'status_id']);
    },
    existingGroupTypes () {
      const typeIds = this.groupValues?.map(item => item.group.type_id);
      return [...new Set(typeIds)];
    },
    groupTypeOtherValue () {
      return this.BasicHelper.getConfigValue('onboarding_types', 'code', 'OTHER', 'id');
    },
    // Onboarding rows grouped by checklist type (Sagatavošanās, 1. darba diena, ...),
    // each group opened by a title row so the checklists don't run into each other.
    tableItems () {
      if (!this.groupValues || this.mode !== 'onboarding') {
        return this.groupValues;
      }
      const sections = [];
      this.groupValues.forEach((item) => {
        const typeId = item.group?.type_id ?? null;
        let section = sections.find(el => el.typeId === typeId);
        if (!section) {
          section = { typeId, items: [] };
          sections.push(section);
        }
        section.items.push(item);
      });
      return sections.flatMap(section => [
        { _isCustomRow: true, title: this.getTypeTitle(section.typeId, section.items[0]) },
        ...section.items
      ]);
    }
  },
  watch: {
    // The checklist picker is shown only for some types, so a pick made under another type would
    // otherwise stay hidden and be added instead of the chosen type's checklists (HR-338).
    'filter.type' () {
      this.$set(this.filter, 'group', null);
    }
  },
  methods: {
    staysEditable (group) {
      return group?.params?.allow_editing_after_saved === true;
    },
    canSaveItems (groupValue) {
      if (!groupValue.has_clickable_checkboxes || !this.hasWritePermissions(groupValue.group.permissions)) {
        return false;
      }

      return this.staysEditable(groupValue.group) || !groupValue.status_id;
    },
    // The send button is worth offering while a switched on toggle still has someone to write to -
    // that is what lets a checklist that was already sent reach only its new recipients (HR-339).
    canSendMail (groupValue) {
      if (!groupValue.value) {
        return false;
      }

      return groupValue.status_id !== this.statusSentMailId || this.hasUnsentToggles(groupValue);
    },
    // Read from the toggles as they are on screen, so a recipient switched on after the checklist was
    // sent brings the button back straight away instead of after a reload (HR-338).
    hasUnsentToggles (groupValue) {
      // A request row writes to the recipients set on the row itself, so any piece ticked since it was
      // sent is something to send - except the comment, which alone would only repeat the list (HR-338).
      if (groupValue.group.params?.send_only_new_toggles === true) {
        return groupValue.group.items.some(item =>
          item.value === true && !item.mail_sent_at && item.code !== 'item_inventory_comment'
        );
      }

      return groupValue.group.items.some(item =>
        item.value === true && !item.mail_sent_at && this.mailPreviewFor(groupValue.id, item)
      );
    },
    isFieldDisabled (item, group, data) {
      if (!this.hasWritePermissions(group.permissions)) {
        return true;
      }
      if (!this.hasWritePermissions(item.permissions)) {
        return true;
      }
      if (item.params?.is_disabled === true) {
        return true;
      }
      // A toggle that has written to its recipients is done with; the rest of a checklist marked as
      // editable stays open, so a forgotten recipient can be switched on and sent afterwards (HR-339).
      if (item.mail_sent_at) {
        return true;
      }
      if ((data.first_saved_at || (data.status_id && data.value)) && !this.staysEditable(group)) {
        return true;
      }

      return false;
    },
    isSaveDisabled (group) {
      if (group.params?.group_checkbox_validation?.includes('atleast_one_checked') &&
        !group.items.some(item => item.value === true)
      ) {
        return true;
      }
    },
    bttnAction (item, action) {
      if (action.params?.update_group_checkbox && !item.value) {
        this.save(item.id, null, null, { group_value_value: true }, { open_url: action.url })
      } else if (action.url) {
        window.open(action.url, '_blank');
      }
    },
    groupStatuses (id, statuses) {
      if (statuses && id) {
        return statuses.find(item => item.id === id)?.name;
      }
    },
    getTypeTitle (typeId, item) {
      const name = item.group?.type?.name;
      if (name) {
        return typeof name === 'object' ? (name[this.$i18n.locale] ?? name.lv) : name;
      }
      return this.checklistTypes.find(el => el.value === typeId)?.text ?? '';
    },
    getRowClass (item) {
      if (item._isCustomRow) {
        return 'onboarding-section-row';
      }
      if (!this.hasWritePermissions(item.group.permissions)) {
        return 'row-disabled';
      }
      // An exit row is finished when it says what became of the thing, not when it is switched on -
      // switching it on is what opens the two buttons (HR-339).
      if (this.mode === 'offboarding') {
        return this.markedReturnStatusIds.includes(item.status_id) ? 'row-disabled' : null;
      }
      if (item.status_id && item.value && !item?.group.params?.row_always_not_disabled) {
        return 'row-disabled';
      }

      return null;
    },
    hasWritePermissions (permissions) {
      if (!permissions || permissions.length < 1) {
        return true;
      }
      if (permissions.some(obj => obj.permission === 'w')) {
        return true;
      }
      return false;
    },
    // Switching the row on says the employee had the thing, and only then is there anything to
    // return: the buttons follow the checkbox and leave once the answer is recorded. Not every exit
    // row starts as "Izsniegts" either - the ones the exit always shows are created without a status
    // at all - so the status is read for what it rules out, not for what it requires (HR-277, HR-339).
    // Onboarding only issues things, so it never asks whether they came back.
    canMarkReturn (item) {
      return this.mode === 'offboarding' && item.value === true && !this.markedReturnStatusIds.includes(item.status_id);
    },
    attemptChangeStatus (item, checkForComment = false) {
      if (!this.validateGroupItems(item.group)) {
        this.$set(item, 'value', false);
        return;
      }
      this.$set(item, 'value', true);
      this.$forceUpdate();
      if (checkForComment) {
        if (item.group && item.group.params && item.group.params.has_comment) {
          item.show_comment = !item.show_comment;
          return;
        }
      }
      if (item.value === true) {
        item.show_comment = false;
        this.save(item.id, item.comment);
      }
    },
    clearFilters () {
      this.filterValues = this.BasicHelper.cloneObject(defaultFilterValues);
      this.loadGroupValues();
    },
    // The API decides this - it knows the HR LDAP groups the frontend cannot see. Deciding it
    // here meant HR got no delete button for rows the API would have removed (HR-200).
    canDeleteItem () {
      return this.canDelete;
    },
    deleteItem (el) {
      this.isUpdating = true;
      this.ApiRequest.request(`onboarding/groups/${el.id}`, this.ApiRequest.REQUEST_DELETE, {}, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, self);
        } else {
          const row = this.groupValues.find(value => value.id === el.id);
          const index = this.groupValues.indexOf(row);
          if (index > -1) {
            this.groupValues.splice(index, 1);
          }
        }
        this.isUpdating = false;
      });
    },
    updateFilters () {
      this.BasicHelper.updateFilterValues(this.$route.name, this.filterValues);
      this.loadGroupValues();
    },
    updateGroupCheckbox (groupValueId, value, group) {
      if (group.params?.group_checkbox_validation?.includes('additional_fields')) {
        // we dont know when user stops filling additional fields so we cant auto check group checkbox
        return;
      }

      const isValid = this.validateGroupItems(group);

      if (group.has_group_checkbox && value === true && isValid === true) {
        // @TODO is this for all checkboxes?
        // const el = this.groupValues.find(el => el.id === groupValueId);
        // this.$set(el, 'value', true);
      }
    },
    changeParamValue (val) {
      const item = this.groupValues.find(el => el.id === val.group_value_id).group.items.find(el => el.id === val.item_id);
      if (item) {
        this.$set(item, 'param_values', val.value);
      }
    },
    getGroupStatuses (groupId) {
      return this.onboardingGroupStatuses.filter(item => item.group_id === groupId);
    },
    changeStatus (item, forceStatus = false, newStatus = null, showComment = false, comment = null) {
      if (showComment) {
        const groupValue = this.groupValues.find(groupValue => groupValue.id == item.id);
        if (groupValue) {
          groupValue.show_issued_comment = true;
        }
        return;
      }
      // The dropdown flow could only move a row that already carried a status. The buttons name the
      // status they set, so they have to go through even on a row that starts without one - the exit
      // checklists the exit always shows are created that way (HR-277).
      if (!newStatus) {
        if (!item.status_id) {
          item.comment = item.additional_values?.comment ?? null;
          if (item.additional_values) {
            item.additional_values.comment = null;
          }
          item.value = false;
        }
        if (!item.status_id && forceStatus === false) {
          return;
        }
      }
      item.force_status = forceStatus;
      this.isUpdating = true;

      // old logic for dropdown
      const data = {
        id: item.id,
        status_id: item.status_id,
        force_status: forceStatus,
        default_status_id: item.group.default_status_id
      }
      // new logic for bttns
      if (newStatus) {
        data.new_status = newStatus;
        data.comment = comment;

        const groupValue = this.groupValues.find(el => el.id === item.id);
        groupValue.show_issued_comment = false;
        groupValue.issued_comment = null;
      }
      this.ApiRequest.request(`user/${this.userApiId}/onboarding/change-status`, this.ApiRequest.REQUEST_PATCH, data, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          const groupValue = this.groupValues.find(el => el.id === item.id);
          groupValue.status_id = response.data.status_id;
          if (response.data.last_status) {
            groupValue.last_status = response.data.last_status;
          }
          if (response.data.approver) {
            groupValue.approvals = [{
              created_at: response.data.approver.created_at,
              user_simple: { fullname: response.data.approver.fullname }
            }];
          }
          this.$store.commit('toastAdd', { context: 'success', message: this.$t('Nomainīts') });
        }
        this.isUpdating = false;
      });
    },
    toggleCheckboxes (groupValueId) {
      const groupValue = this.groupValues.find(el => el.id === groupValueId);
      groupValue.show_additional_checkboxes = !groupValue.show_additional_checkboxes;
    },
    loadGroupValues () {
      if (this.currentReq) {
        this.currentReq.cancel('changed search query');
      }
      this.isLoading = true;
      this.currentReq = this.ApiRequest.request(this.requestPath, this.ApiRequest.REQUEST_GET, [], (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          this.groupValues = response.data.items;
          this.user = response.data.user;
          this.existing_group_ids = response.data.existing_group_ids;
          this.canDelete = response.data.can_delete === true;
          this.loadMailPreview();
        }
        this.isLoading = false;
      });
    },
    // What each toggle would send and to whom. One request covers the whole checklist, because the
    // answer differs per row and per employee, not per toggle.
    loadMailPreview () {
      const groupValueIds = (this.groupValues ?? []).map(groupValue => groupValue.id);
      if (!groupValueIds.length) {
        this.mailPreview = {};
        return;
      }
      this.isLoadingMailPreview = true;
      // Built by hand: the shared helper joins arrays with semicolons, which is not a list to Laravel.
      const query = groupValueIds.map(id => `group_value_ids[]=${id}`).join('&');
      const path = `user/${this.userApiId}/onboarding/mail-recipients?${query}`;
      this.ApiRequest.request(path, this.ApiRequest.REQUEST_GET, [], (response) => {
        // A failed preview must not block the checklist itself, so the rows simply stay without it.
        this.mailPreview = response.error ? {} : response.data;
        this.isLoadingMailPreview = false;
      });
    },
    mailPreviewFor (groupValueId, item) {
      return this.mailPreview[groupValueId]?.[item.id] ?? null;
    },
    // The office direct manager row has no toggles - it writes to the recipient set on the row itself -
    // so its message is shown on the row, while it is switched on and not yet sent (HR-338). The API
    // returns it only for rows marked with show_row_mail_preview.
    rowMailPreviewFor (groupValue) {
      if (!groupValue.value || groupValue.status_id === this.statusSentMailId) {
        return null;
      }

      return this.mailPreview[groupValue.id]?.group ?? null;
    },
    add () {
      this.isLoading = true;
      this.ApiRequest.request(`user/${this.userApiId}/onboarding`, this.ApiRequest.REQUEST_POST, this.filter, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          this.loadGroupValues();
        }
      });
    },
    async save (groupValueid, comment = null, group = null, params = null, afterSave = null) {
      if (group) {
        const isValid = this.validateGroupItems(group);
        if (!isValid) {
          this.$store.commit('toastAdd', {
            context: 'danger',
            message: this.$t('general.fillRequiredMessage')
          });
          return false;
        } else {
          // we can rely on validation only if all fields are required. Otherwise we dont know if user has finished checking boxes
          if (group.params?.group_checkbox_validation?.includes('all_values_true') || group.params?.group_checkbox_validation?.includes('atleast_one_checked')) {
            if (params) {
              params.group_value_value = true;
            } else {
              params = { group_value_value: true };
            }
          }
        }
      }
      const item = this.groupValues.find(item => item.id === groupValueid);
      if (!item) {
        return false;
      }
      let groupValueValue = item.value;
      if (params?.group_value_value === true) {
        groupValueValue = true;
      }
      this.isUpdating = true;
      const postData = {
        group_value_id: item.id,
        group_value_value: groupValueValue,
        comment: comment
      }
      if (item.has_clickable_checkboxes) {
        postData.checkboxes = item.group.items;
      }
      return await new Promise(resolve => this.ApiRequest.request(`user/${this.userApiId}/onboarding`, this.ApiRequest.REQUEST_PATCH, postData, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          this.$store.commit('toastAdd', { context: 'success', message: this.$t('Saglabāts') });
          const groupValue = this.groupValues.find(el => el.id === item.id);
          if (response?.data?.status_changed) {
            groupValue.status_id = response.data.status_changed;
            this.$store.commit('toastAdd', { context: 'success', message: this.$t('Statuss nomainīts') });
          }
          if (response?.data?.value) {
            groupValue.value = response.data.value;
          }
          if (response?.data?.has_true_values) {
            groupValue.has_true_values = response.data.has_true_values;
          }
          if (response?.data?.first_saved_at) {
            groupValue.first_saved_at = response.data.first_saved_at;
          }
          if (response?.data?.comment) {
            groupValue.additional_values = { comment: response.data.comment };
          }
          if (response?.data?.approver) {
            const approver = response.data.approver;
            groupValue.approvals = [];
            groupValue.approvals.push({
              created_at: approver.created_at,
              user_simple: {
                fullname: approver.fullname
              }
            });
          }
          if (afterSave?.open_url) {
            window.open(afterSave.open_url, '_blank');
          }
          this.$forceUpdate();
        }
        this.isUpdating = false;
        resolve(!response.error);
      }));
    },
    async doAction (groupValueId, action) {
      this.isUpdating = true;
      this.ApiRequest.request(`user/${this.userApiId}/onboarding/actions`, this.ApiRequest.REQUEST_POST, { action: action, group_value_id: groupValueId }, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else {
          if (response?.data?.approver) {
            const approver = response.data.approver;
            const groupValue = this.groupValues.find(el => el.id === groupValueId);
            groupValue.approvals = [];
            groupValue.approvals.push({
              created_at: approver.created_at,
              user_simple: {
                fullname: approver.fullname
              }
            });
          }
          this.$store.commit('toastAdd', { context: 'success', message: this.$t('Nosūtīts') });
        }
        this.isUpdating = false;
        this.loadGroupValues();
      });
    },
    // The row is saved first and the mail sent only once the save has answered, so the mail carries
    // the comment and toggles just written instead of racing them - the EBV work clothing request sends
    // its comment to the warehouse (HR-338).
    async doActionCombined (groupValueid, groupId = null, action = null) {
      const groupValue = this.groupValues.find(el => el.id === groupValueid);
      if (!await this.save(groupValueid, groupValue?.comment || null, null, { group_value_value: true })) {
        return;
      }
      await this.doAction(groupValueid, action);
    },
    validateGroupItems (group) {
      let isValid = true;
      if (group?.params?.group_checkbox_validation?.includes('all_values_true')) {
        if (group.items?.some(obj => obj.params?.validation?.includes('required') && obj.value === false)) {
          return false;
        }
      }
      if (group.params?.group_checkbox_validation?.includes('additional_fields')) {
        group.items?.forEach((item) => {
          if (item.params?.fields?.length > 0) {
            // right now item may have only 1 additional field; validate it
            if (item.params.fields[0].validation && item.params.fields[0].validation.includes('required') &&
              !item.param_values
            ) {
              isValid = false;
              // @TODO break/exit
            }
          }
        });
      }
      return isValid;
    }
  },
  mounted () {
    this.filterValues = this.BasicHelper.fillFilterValues(this.$route.name, this.filterValues, this.$session);
    this.loadGroupValues();
  }
}
